// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a machine's health and activity (docs/konvo-agents/HEALTH.md, design/agents-health C-open-
// desk-m1, C-phone-m1, sheet-phone-m1, picker-desk-g2). In Settings → Connections → Agents each
// machine card gets a Health section: closed, the worst badge, the main problem in words and its fix;
// open, every check and the three newest events with "See all activity". The activity log is a
// dialog on desktop and a sheet on phones: Everything / Problems, by day, problems open into what
// happened, what Konvo did, the code and ref, the versions, "Copy for support" and "Open the chat".
// The Agents list shows a bad machine's main problem under the machine picker. Team insights uses
// the same checks and log, read-only.

import {Routes} from '@app/app/Routes';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {openAccountSignIn} from '@app/features/konvo_agents/components/AgentAccountsSettings';
import {CommandBlock, useNow} from '@app/features/konvo_agents/components/AgentBits';
import styles from '@app/features/konvo_agents/components/AgentHealth.module.css';
import {
	checkName,
	checkState,
	dayHeading,
	eventKonvoDid,
	eventSub,
	eventTimes,
	eventTitle,
	healthSummary,
	problemText,
	problemWhy,
	type ReportMachine,
	supportReport,
	versionsText,
} from '@app/features/konvo_agents/components/AgentHealthCopy';
import {agentErrorRef, agentServerMessage, clockTime, itemTime} from '@app/features/konvo_agents/components/AgentsCopy';
import {machineConnections} from '@app/features/konvo_agents/state/AgentConnections';
import AgentActivity, {
	type ActivityFilter,
	type ActivitySource,
	filterActivity,
	groupByDay,
	isProblemEvent,
	mainProblem,
} from '@app/features/konvo_agents/state/AgentHealth';
import Agents from '@app/features/konvo_agents/state/Agents';
import {agentErrorCode, platformName} from '@app/features/konvo_agents/state/AgentsModel';
import type {AgentToolProvider} from '@app/features/konvo_agents/state/AgentToolUpdates';
import {scrollToInList} from '@app/features/konvo_agents/utils/AgentListScroll';
import {transitionTo} from '@app/features/navigation/utils/RouterUtils';
import {Accordion} from '@app/features/ui/accordion/Accordion';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {
	ActionButton,
	Notice,
	Segmented,
	StatusBadge,
	type StatusState,
} from '@app/features/ui/konvo_controls/KonvoControls';
import * as Sheet from '@app/features/ui/sheet/Sheet';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {
	AGENT_ACTIVITY_KEEP_DAYS,
	type AgentActivityEvent,
	type AgentHealthCheck,
	type AgentMachineView,
} from '@fluxer/schema/src/domains/konvo/Agents';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	IconActivity,
	IconChevronDown,
	IconCloudDownload,
	IconCopy,
	IconLogin2,
	IconMessageCircle,
	IconPencil,
	IconPlugConnected,
	IconRefresh,
	IconUserMinus,
	IconUserPlus,
} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef, useState} from 'react';

const LOG_KEY = 'konvo-agents-activity';

/** The tool's name on a machine, for checks and events ("T3 Code"). */
export function toolNameOf(machine: Pick<AgentMachineView, 'tools'>): string {
	return machine.tools[0]?.name ?? 'T3 Code';
}

/** A machine as the log and "Copy for support" name it. */
export function reportMachine(machine: AgentMachineView): ReportMachine {
	return {
		name: machine.name,
		platform: platformName(machine.platform) || undefined,
		bridgeVersion: machine.bridgeVersion,
		toolName: toolNameOf(machine),
		toolVersion: machine.tools[0]?.version,
	};
}

const levelState = (event: AgentActivityEvent): StatusState => (event.level === 'error' ? 'bad' : 'warn');

/** An event's mark: a status badge for problems, a quiet icon for the rest. */
function EventMark({event}: {event: AgentActivityEvent}) {
	if (isProblemEvent(event)) return <StatusBadge state={levelState(event)} />;
	const icon = {size: 18, stroke: 1.7} as const;
	switch (event.kind) {
		case 'signedIn':
			return <IconLogin2 {...icon} className={styles.infoIcon} />;
		case 'connected':
		case 'toolUp':
			return <IconPlugConnected {...icon} className={styles.infoIcon} />;
		case 'accountAdded':
			return <IconUserPlus {...icon} className={styles.infoIcon} />;
		case 'accountRemoved':
			return <IconUserMinus {...icon} className={styles.infoIcon} />;
		case 'accountChanged':
			return <IconPencil {...icon} className={styles.infoIcon} />;
		default:
			return <IconCloudDownload {...icon} className={styles.infoIcon} />;
	}
}

// ── Checks ──────────────────────────────────────────────────────────────

/** One check: badge, name, short state; a bad one also says the machine's words and its command. */
function CheckCell({
	check,
	toolName,
	now,
	commandFor,
}: {
	check: AgentHealthCheck;
	toolName: string;
	now: number;
	/** Who runs a fix command by hand ("On the machine, Berno runs:"); omit for the owner. */
	commandFor?: string;
}) {
	const {i18n} = useLingui();
	const name = checkName(i18n, check, toolName);
	const showCommand = check.fix === 'command' && !!check.command;
	return (
		<div
			className={clsx(styles.check, check.state === 'bad' && styles.checkBad)}
			data-flx="konvo-agents.health.check"
			data-check={check.id}
			data-state={check.state}
		>
			<StatusBadge state={check.state} />
			<div className={styles.checkBody}>
				<div className={styles.checkLine}>
					<span className={styles.checkName}>{name}</span>
					<span className={styles.checkState}>{checkState(i18n, check, now)}</span>
				</div>
				{check.detail && check.state !== 'ok' && <p className={styles.checkDetail}>{check.detail}</p>}
				{showCommand && (
					<>
						{commandFor && (
							<p className={styles.checkDetail}>
								<Trans comment="Team insights machine dialog: above the command its owner runs; {person} is their name.">
									On the machine, {commandFor} runs:
								</Trans>
							</p>
						)}
						<CommandBlock lines={[check.command!]} wrap data-flx="konvo-agents.health.check.command" />
					</>
				)}
			</div>
		</div>
	);
}

/** Every check in a grid: two columns, one on phones. */
export function HealthChecks({
	checks,
	toolName,
	now,
	commandFor,
	single = false,
}: {
	checks: ReadonlyArray<AgentHealthCheck>;
	toolName: string;
	now: number;
	commandFor?: string;
	/** One column (dialogs, phones). */
	single?: boolean;
}) {
	return (
		<div className={clsx(styles.checks, single && styles.checksSingle)} data-flx="konvo-agents.health.checks">
			{checks.map((check) => (
				<CheckCell key={check.id} check={check} toolName={toolName} now={now} commandFor={commandFor} />
			))}
		</div>
	);
}

// ── Fixes ───────────────────────────────────────────────────────────────

const COPIED = msg({message: 'Command copied', comment: 'Agents health: toast after copying a fix command.'});

/** The button that fixes a check from Konvo: Sign in, Update, or Copy command. */
export const HealthFix = observer(
	({machine, check, small = true}: {machine: AgentMachineView; check: AgentHealthCheck; small?: boolean}) => {
		const {i18n} = useLingui();
		const [busy, setBusy] = useState(false);
		if (check.fix === 'signIn') {
			const connection = machineConnections(machine).find((entry) => entry.provider === check.provider);
			if (!connection?.loginCommand && !check.command) return null;
			return (
				<ActionButton
					tone="danger"
					small={small}
					onClick={(event) => {
						event.stopPropagation();
						if (connection) openAccountSignIn(machine, connection);
						else void TextCopyCommands.copy(i18n, check.command!);
					}}
					data-flx="konvo-agents.health.fix.sign-in"
				>
					<Trans comment="Agents health: button that signs an account in again.">Sign in</Trans>
				</ActionButton>
			);
		}
		if (check.fix === 'updateAgent') {
			const provider = (
				/codex/i.test(check.driver ?? check.provider ?? '') ? 'codex' : 'claudeAgent'
			) as AgentToolProvider;
			return (
				<ActionButton
					tone="secondary"
					small={small}
					disabled={busy || !machine.online}
					onClick={(event) => {
						event.stopPropagation();
						setBusy(true);
						void Agents.updateAgentTool(machine.id, provider).finally(() => setBusy(false));
					}}
					data-flx="konvo-agents.health.fix.update"
				>
					<Trans comment="Agents health: button that updates Claude Code or Codex on the machine.">Update</Trans>
				</ActionButton>
			);
		}
		if (check.fix === 'command' && check.command) {
			const command = check.command;
			return (
				<ActionButton
					tone="secondary"
					small={small}
					icon={<IconCopy size={15} stroke={1.8} />}
					onClick={(event) => {
						event.stopPropagation();
						void TextCopyCommands.copy(i18n, command, true).then((done) => {
							if (done) ToastCommands.success(i18n._(COPIED));
						});
					}}
					data-flx="konvo-agents.health.fix.command"
				>
					<Trans comment="Agents health: copies the command that fixes a problem, to run on the machine.">
						Copy command
					</Trans>
				</ActionButton>
			);
		}
		return null;
	},
);

// ── Recent activity on the card ────────────────────────────────────────

function RecentRow({event, toolName, now}: {event: AgentActivityEvent; toolName: string; now: number}) {
	const {i18n} = useLingui();
	const sub = eventSub(i18n, event, now);
	const times = eventTimes(i18n, event);
	const at = Date.parse(event.lastAt ?? event.at);
	return (
		<li className={styles.recentRow} data-flx="konvo-agents.health.recent.row" data-kind={event.kind}>
			<span className={styles.mark}>
				<EventMark event={event} />
			</span>
			<span className={styles.recentText}>
				<span className={styles.recentTitle}>{eventTitle(i18n, event, toolName)}</span>
				{times && <span className={styles.sub}> · {times}</span>}
				{sub && <span className={styles.sub}> · {sub}</span>}
			</span>
			<time className={styles.time} dateTime={event.lastAt ?? event.at}>
				{itemTime(i18n, at, now)}
			</time>
		</li>
	);
}

// ── The Health section of a machine card ───────────────────────────────

/** Settings → Connections → Agents: the machine's Health, closed to one line, open to every check. */
export const MachineHealthSection = observer(({machine}: {machine: AgentMachineView}) => {
	const {i18n} = useLingui();
	const now = useNow(60_000);
	const health = machine.health;
	if (!health) return null;
	const toolName = toolNameOf(machine);
	const summary = healthSummary(i18n, health, machine, toolName, now);
	const state: StatusState = machine.online ? health.state : 'off';
	const recent = machine.recentActivity ?? [];
	const phone = MobileLayout.enabled;
	const keepDays = AGENT_ACTIVITY_KEEP_DAYS;
	return (
		<div className={styles.section} data-flx="konvo-agents.health" data-state={state}>
			<Accordion
				id={`agents-health-${machine.id}`}
				compact
				className={styles.accordion}
				title={
					<span className={styles.headline}>
						<StatusBadge state={state} />
						<span className={styles.lead} data-flx="konvo-agents.health.lead">
							<b>{summary.lead}</b>
							<span className={styles.leadRest}>
								<Trans comment="Agents Settings: names a machine's health section, under its main line.">Health</Trans>
								{!phone && summary.rest.length > 0 && ` · ${summary.rest.join(' · ')}`}
							</span>
						</span>
					</span>
				}
				headerAction={summary.main && machine.online ? <HealthFix machine={machine} check={summary.main} /> : undefined}
			>
				<div className={styles.open} data-flx="konvo-agents.health.open">
					<HealthChecks checks={health.checks} toolName={toolName} now={now} single={phone} />
					<div className={styles.recent}>
						<span className={styles.label}>
							<Trans comment="Agents Settings: heading over a machine's newest events.">Activity</Trans>
						</span>
						{recent.length ? (
							<ul className={styles.recentList}>
								{recent.slice(0, 3).map((event) => (
									<RecentRow key={event.id} event={event} toolName={toolName} now={now} />
								))}
							</ul>
						) : (
							<p className={styles.quiet}>
								<Trans comment="Agents Settings: a machine has no activity yet.">Nothing has happened yet.</Trans>
							</p>
						)}
						<div className={styles.recentFoot}>
							<span className={styles.quiet}>
								<Trans comment="Agents Settings: how long Konvo keeps a machine's activity; {keepDays} is a number of days.">
									Konvo keeps {keepDays} days · never chat text
								</Trans>
							</span>
							<button
								type="button"
								className={styles.link}
								onClick={() =>
									openAgentActivity({source: 'own', machine: reportMachine(machine), machineId: machine.id})
								}
								data-flx="konvo-agents.health.see-all"
							>
								<Trans comment="Agents Settings: opens a machine's full activity log.">See all activity</Trans>
							</button>
						</div>
					</div>
				</div>
			</Accordion>
		</div>
	);
});

// ── The problem under the machine picker ───────────────────────────────

/** Agents list: a bad machine's main problem and its fix, under the machine picker. */
export const MachineProblem = observer(({machine, phone = false}: {machine: AgentMachineView; phone?: boolean}) => {
	const {i18n} = useLingui();
	const now = useNow(60_000);
	const health = machine.health;
	if (!machine.online || health?.state !== 'bad') return null;
	const main = mainProblem(health);
	if (!main) return null;
	const toolName = toolNameOf(machine);
	const why = problemWhy(i18n, main, toolName);
	return (
		<div className={clsx(styles.problem, phone && styles.problemPhone)} data-flx="konvo-agents.list.machine.problem">
			<StatusBadge state="bad" />
			<span className={styles.problemText}>
				<b>{problemText(i18n, main, toolName, now)}</b>
				{why && <span className={styles.sub}>{why}</span>}
			</span>
			<HealthFix machine={machine} check={main} />
		</div>
	);
});

// ── The activity log ───────────────────────────────────────────────────

const WHAT_HAPPENED = msg({message: 'What happened', comment: 'Agents activity details: label.'});
const WHAT_KONVO_DID = msg({message: 'What Konvo did', comment: 'Agents activity details: label.'});
const CODE = msg({message: 'Code', comment: 'Agents activity details: label over the error code and ref.'});
const VERSIONS = msg({message: 'Versions', comment: 'Agents activity details: label over the versions.'});
const REF = msg({
	message: '{code} · ref {ref}',
	comment: 'Agents activity details: the error code and Konvo’s reference, e.g. "T3_ERROR · ref k-7f3a2c".',
});
const REF_ONLY = msg({message: 'ref {ref}', comment: 'Agents activity details: Konvo’s reference for the event.'});

/** One event of the log; a problem opens into its details. */
const EventRow = observer(
	({
		event,
		machine,
		now,
		open,
		onToggle,
		onOpenChat,
	}: {
		event: AgentActivityEvent;
		machine: ReportMachine;
		now: number;
		open: boolean;
		onToggle: () => void;
		onOpenChat?: (threadId: string) => void;
	}) => {
		const {i18n} = useLingui();
		const problem = isProblemEvent(event);
		const sub = eventSub(i18n, event, now);
		const times = eventTimes(i18n, event);
		const title = eventTitle(i18n, event, machine.toolName);
		const did = eventKonvoDid(i18n, event);
		const versions = versionsText(
			i18n,
			event.bridgeVersion ?? machine.bridgeVersion,
			machine.toolName,
			event.toolVersion ?? machine.toolVersion,
		);
		const at = Date.parse(event.lastAt ?? event.at);
		const body = (
			<>
				<span className={styles.mark}>
					<EventMark event={event} />
				</span>
				<span className={styles.eventText}>
					<span className={styles.eventTitle}>
						{title}
						{times && <span className={styles.sub}> · {times}</span>}
					</span>
					{sub && <span className={styles.eventSub}>{sub}</span>}
				</span>
				<time className={styles.time} dateTime={event.lastAt ?? event.at}>
					{clockTime(i18n, at)}
				</time>
				{problem && (
					<IconChevronDown size={16} stroke={1.8} className={clsx(styles.chevron, open && styles.chevronOpen)} />
				)}
			</>
		);
		return (
			<li
				className={clsx(styles.event, open && styles.eventOpen)}
				data-flx="konvo-agents.activity.event"
				data-kind={event.kind}
				data-level={event.level}
				data-event={event.id}
			>
				{problem ? (
					<button
						type="button"
						className={styles.eventHead}
						onClick={onToggle}
						aria-expanded={open}
						data-flx="konvo-agents.activity.event.toggle"
					>
						{body}
					</button>
				) : (
					<div className={styles.eventHead}>{body}</div>
				)}
				{problem && open && (
					<div className={styles.details} data-flx="konvo-agents.activity.event.details">
						<dl className={styles.facts}>
							<dt>{i18n._(WHAT_HAPPENED)}</dt>
							<dd>{event.detail || title}</dd>
							{did && (
								<>
									<dt>{i18n._(WHAT_KONVO_DID)}</dt>
									<dd>{did}</dd>
								</>
							)}
							<dt>{i18n._(CODE)}</dt>
							<dd className={styles.mono} data-flx="konvo-agents.activity.event.ref">
								{event.code ? i18n._(REF, {code: event.code, ref: event.id}) : i18n._(REF_ONLY, {ref: event.id})}
							</dd>
							{versions && (
								<>
									<dt>{i18n._(VERSIONS)}</dt>
									<dd>{versions}</dd>
								</>
							)}
						</dl>
						<div className={styles.detailActions}>
							<ActionButton
								small
								icon={<IconCopy size={15} stroke={1.8} />}
								onClick={() => void TextCopyCommands.copy(i18n, supportReport(i18n, machine, event))}
								data-flx="konvo-agents.activity.event.copy"
							>
								<Trans comment="Agents activity: copies a plain-text report of the event for support.">
									Copy for support
								</Trans>
							</ActionButton>
							{event.threadId && onOpenChat && (
								<ActionButton
									small
									icon={<IconMessageCircle size={15} stroke={1.8} />}
									onClick={() => onOpenChat(event.threadId!)}
									data-flx="konvo-agents.activity.event.open-chat"
								>
									<Trans comment="Agents activity: opens the chat the event happened in.">Open the chat</Trans>
								</ActionButton>
							)}
						</div>
					</div>
				)}
			</li>
		);
	},
);

/** Events by day, with Everything / Problems; problems open into their details. */
export const ActivityList = observer(
	({
		events,
		machine,
		focusId,
		onOpenChat,
	}: {
		events: ReadonlyArray<AgentActivityEvent>;
		machine: ReportMachine;
		/** Opened at this event (from a failed send's "See activity"). */
		focusId?: string | null;
		onOpenChat?: (threadId: string) => void;
	}) => {
		const {i18n, t} = useLingui();
		const now = useNow(60_000);
		const [filter, setFilter] = useState<ActivityFilter>('all');
		const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(focusId ? [focusId] : []));
		const listRef = useRef<HTMLDivElement>(null);
		const problems = events.filter(isProblemEvent).length;
		const days = groupByDay(filterActivity(events, filter));
		useEffect(() => {
			if (!focusId) return;
			const row = listRef.current?.querySelector<HTMLElement>(`[data-event="${CSS.escape(focusId)}"]`);
			scrollToInList(row);
		}, [focusId, events.length]);
		const toggle = (id: string) =>
			setOpen((current) => {
				const next = new Set(current);
				if (next.has(id)) next.delete(id);
				else next.add(id);
				return next;
			});
		return (
			<div className={styles.log} ref={listRef}>
				<Segmented
					grow
					compact
					value={filter}
					onChange={setFilter}
					aria-label={t`Show`}
					options={[
						{
							value: 'all',
							children: t({message: 'Everything', comment: 'Agents activity: show every event.'}),
							'data-flx': 'konvo-agents.activity.filter.all',
						},
						{
							value: 'problems',
							children: t({message: 'Problems', comment: 'Agents activity: show only warnings and errors.'}),
							count: problems,
							'data-flx': 'konvo-agents.activity.filter.problems',
						},
					]}
					data-flx="konvo-agents.activity.filter"
				/>
				{days.length === 0 ? (
					<p className={styles.quiet} data-flx="konvo-agents.activity.empty">
						{filter === 'problems' ? (
							<Trans comment="Agents activity: no warnings or errors were kept.">No problems. Nothing failed.</Trans>
						) : (
							<Trans comment="Agents activity: nothing was kept for the machine yet.">Nothing has happened yet.</Trans>
						)}
					</p>
				) : (
					days.map((day) => (
						<section key={day.day} className={styles.day} data-flx="konvo-agents.activity.day">
							<h4 className={styles.dayHeading}>{dayHeading(i18n, day.start, now)}</h4>
							<ul className={styles.dayList}>
								{day.events.map((event) => (
									<EventRow
										key={event.id}
										event={event}
										machine={machine}
										now={now}
										open={open.has(event.id)}
										onToggle={() => toggle(event.id)}
										onOpenChat={onOpenChat}
									/>
								))}
							</ul>
						</section>
					))
				)}
			</div>
		);
	},
);

/** The log's body for a machine: loads its activity and shows the list (or why it can't). */
export const ActivityBody = observer(
	({
		source,
		machineId,
		machine,
		focusId,
		onOpenChat,
	}: {
		source: ActivitySource;
		machineId: string;
		machine: ReportMachine;
		focusId?: string | null;
		onOpenChat?: (threadId: string) => void;
	}) => {
		useEffect(() => {
			void AgentActivity.load(source, machineId);
		}, [source, machineId]);
		const load = AgentActivity.of(source, machineId);
		if (!load?.data)
			return (
				<div className={styles.loadState}>
					{load?.failed ? (
						<>
							<p className={styles.quiet}>
								<Trans comment="Agents activity: the list did not load.">
									Konvo didn't answer. Try again in a minute.
								</Trans>
							</p>
							<ActionButton
								small
								icon={<IconRefresh size={15} stroke={1.8} />}
								onClick={() => void AgentActivity.load(source, machineId)}
							>
								<Trans>Try again</Trans>
							</ActionButton>
						</>
					) : (
						<p className={styles.quiet}>
							<Trans comment="Agents activity: the list is loading.">Loading…</Trans>
						</p>
					)}
				</div>
			);
		return <ActivityList events={load.data.events} machine={machine} focusId={focusId} onOpenChat={onOpenChat} />;
	},
);

const ACTIVITY_TITLE = msg({message: 'Activity', comment: 'Agents activity log: title.'});
const ACTIVITY_SUB = msg({
	message: '{machine} · last {days} days · never chat text',
	comment: 'Agents activity log: under the title; {machine} is the computer, {days} how many days Konvo keeps.',
});

export interface OpenActivityArgs {
	source: ActivitySource;
	machineId: string;
	machine: ReportMachine;
	focusId?: string | null;
}

function ActivityDialog({args, onClose}: {args: OpenActivityArgs; onClose: () => void}) {
	const {i18n} = useLingui();
	const sub = i18n._(ACTIVITY_SUB, {machine: args.machine.name, days: AGENT_ACTIVITY_KEEP_DAYS});
	const openChat =
		args.source === 'own'
			? (threadId: string) => {
					ModalCommands.popAll();
					transitionTo(Routes.agents(args.machineId, threadId));
				}
			: undefined;
	const body = (
		<ActivityBody
			source={args.source}
			machineId={args.machineId}
			machine={args.machine}
			focusId={args.focusId}
			onOpenChat={openChat}
		/>
	);
	if (MobileLayout.enabled)
		return (
			<Sheet.Root isOpen onClose={onClose} snapPoints={[0, 0.92]} initialSnap={1} surface="secondary">
				<Sheet.Handle />
				<Sheet.Header trailing={<Sheet.CloseButton onClick={onClose} />}>
					<Sheet.Title>{i18n._(ACTIVITY_TITLE)}</Sheet.Title>
					<Sheet.Subtitle>{sub}</Sheet.Subtitle>
				</Sheet.Header>
				<Sheet.Content padding="md">
					<div className={styles.sheetBody} data-flx="konvo-agents.activity.sheet">
						{body}
					</div>
				</Sheet.Content>
			</Sheet.Root>
		);
	return (
		<Modal.Root size="medium" centered onClose={onClose} data-flx="konvo-agents.activity.dialog">
			<Modal.Header title={i18n._(ACTIVITY_TITLE)} onClose={onClose} />
			<Modal.Content>
				<div className={styles.dialogBody}>
					<p className={styles.dialogLead}>{sub}</p>
					{body}
				</div>
			</Modal.Content>
		</Modal.Root>
	);
}

/** Opens a machine's activity log (dialog on desktop, sheet on phones), at an event when given. */
export function openAgentActivity(args: OpenActivityArgs): void {
	const close = () => ModalCommands.popWithKey(LOG_KEY);
	ModalCommands.pushWithKey(
		ModalCommands.modal(() => <ActivityDialog args={args} onClose={close} />),
		LOG_KEY,
	);
}

// ── A prompt that didn't go ────────────────────────────────────────────

const SEND_KEPT = msg({
	message: 'Your prompt is kept above; nothing was lost.',
	comment: 'Agents chat: after why a prompt did not go through to the machine.',
});
const WHEN = msg({message: 'When', comment: 'Agents chat failure details: label over when it failed.'});
const MACHINE = msg({message: 'Machine', comment: 'Agents chat failure details: label over the machine and versions.'});
const MACHINE_LINE = msg({
	message: '{machine} · {state} · {versions}',
	comment: 'Agents chat failure details: e.g. "interns · online · konvo-agents 0.50.0 · T3 Code 0.1.3".',
});
const ONLINE = msg({message: 'online', comment: 'Agents chat failure details: the machine is online.'});
const OFFLINE = msg({message: 'offline', comment: 'Agents chat failure details: the machine is offline.'});

/**
 * Under a prompt that didn't go through: why, with the code, Konvo's ref, when and the machine's
 * versions, and Try again, Edit, Copy for support and See activity (the log, at the event).
 */
export const SendFailure = observer(
	({
		machineId,
		error,
		failedAt,
		reason,
		onRetry,
		onEdit,
		flx,
		uncertain = false,
	}: {
		machineId: string;
		error: unknown;
		failedAt: number;
		/** Why, in words (agentErrorMessage). */
		reason: string;
		onRetry: () => void;
		onEdit: () => void;
		flx: string;
		uncertain?: boolean;
	}) => {
		const {i18n, t} = useLingui();
		const now = useNow(60_000);
		const machine = Agents.machine(machineId);
		const code = agentErrorCode(error);
		const ref = agentErrorRef(error);
		const said = agentServerMessage(error);
		const name = machine?.name ?? '';
		const report = machine ? reportMachine(machine) : {name, toolName: 'T3 Code'};
		const versions = versionsText(i18n, report.bridgeVersion, report.toolName, report.toolVersion);
		const at = new Date(failedAt);
		// To the second today ("14:37:12"), else with the day.
		const when =
			at.toDateString() === new Date(now).toDateString()
				? new Intl.DateTimeFormat(i18n.locale, {hour: '2-digit', minute: '2-digit', second: '2-digit'}).format(at)
				: itemTime(i18n, failedAt, now);
		const copy = () =>
			void TextCopyCommands.copy(
				i18n,
				supportReport(i18n, report, {
					id: ref ?? '',
					at: at.toISOString(),
					level: 'error',
					kind: 'actionFailed',
					op: 'send',
					...(code ? {code} : {}),
					...(said ? {detail: said.slice(0, 400)} : {}),
				}),
			);
		return (
			<Notice
				tone="danger"
				className={styles.failure}
				icon={<StatusBadge state="bad" />}
				title={
					uncertain
						? t({
								message: `Waiting to confirm delivery to ${name}`,
								comment:
									'Agents: a send may have reached the machine but its confirmation was lost; name is the machine.',
							})
						: t({
								message: `Couldn't send to ${name}`,
								comment: 'Agents chat: title of a prompt that did not go through; {name} is the machine.',
							})
				}
				details={
					<dl className={styles.failureFacts} data-flx={`${flx}.details`}>
						{(code || ref) && (
							<>
								<dt>{i18n._(CODE)}</dt>
								<dd className={styles.mono} data-flx={`${flx}.ref`}>
									{code && ref ? i18n._(REF, {code, ref}) : ref ? i18n._(REF_ONLY, {ref}) : code}
								</dd>
							</>
						)}
						<dt>{i18n._(WHEN)}</dt>
						<dd>{when}</dd>
						{machine && (
							<>
								<dt>{i18n._(MACHINE)}</dt>
								<dd>
									{versions
										? i18n._(MACHINE_LINE, {
												machine: name,
												state: i18n._(machine.online ? ONLINE : OFFLINE),
												versions,
											})
										: name}
								</dd>
							</>
						)}
					</dl>
				}
				actions={
					<>
						<ActionButton
							tone="primary"
							small
							icon={<IconRefresh size={15} stroke={1.8} />}
							onClick={onRetry}
							data-flx={`${flx}.retry`}
						>
							<Trans>Try again</Trans>
						</ActionButton>
						<ActionButton small icon={<IconPencil size={15} stroke={1.8} />} onClick={onEdit} data-flx={`${flx}.edit`}>
							<Trans>Edit</Trans>
						</ActionButton>
						<ActionButton small icon={<IconCopy size={15} stroke={1.8} />} onClick={copy} data-flx={`${flx}.copy`}>
							<Trans comment="Agents chat: copies a plain-text report of the failure for support.">
								Copy for support
							</Trans>
						</ActionButton>
						{ref && machine && (
							<ActionButton
								small
								icon={<IconActivity size={15} stroke={1.8} />}
								onClick={() =>
									openAgentActivity({source: 'own', machineId, machine: reportMachine(machine), focusId: ref})
								}
								data-flx={`${flx}.activity`}
							>
								<Trans comment="Agents chat: opens the machine's activity log at this failure.">See activity</Trans>
							</ActionButton>
						)}
					</>
				}
				data-flx={`${flx}.failed`}
			>
				{reason} {i18n._(SEND_KEPT)}
			</Notice>
		);
	},
);
